import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Trophy } from 'lucide-react';
import { db } from '../../firebase/config';
import { buildMonthlyGrowth } from '../../lib/growthStats';
import { cn } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const RANGES = [
  { id: '12m', label: '12 months', months: 12 },
  { id: 'all', label: 'All time', months: null },
];

const fmtMonth = (d, opts = { month: 'short', year: 'numeric' }) =>
  d.toLocaleDateString('en-US', opts).toUpperCase();

const GrowthChart = ({ tournaments }) => {
  const [joinDates, setJoinDates] = useState(null);
  const [error, setError] = useState(false);
  const [range, setRange] = useState('12m');
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'userProfiles'))
      .then((snap) => {
        if (!cancelled) setJoinDates(snap.docs.map((d) => d.data().createdAt));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { buckets, undated } = useMemo(
    () =>
      buildMonthlyGrowth(joinDates || [], tournaments, {
        months: RANGES.find((r) => r.id === range).months,
      }),
    [joinDates, tournaments, range]
  );

  const max = Math.max(1, ...buckets.map((b) => b.joins));
  const windowJoins = buckets.reduce((sum, b) => sum + b.joins, 0);
  const windowEvents = buckets.reduce((sum, b) => sum + b.tournaments.length, 0);
  const active = buckets[hovered ?? buckets.length - 1];
  const labelEvery = Math.ceil(buckets.length / 12);

  return (
    <Card className="mt-6 border-border/60 bg-background/40">
      <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Growth
          </div>
          <CardTitle className="mt-1 font-display text-2xl">
            Player signups &amp; tournaments
          </CardTitle>
        </div>
        <div className="flex border border-border/60" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => {
                setRange(r.id);
                setHovered(null);
              }}
              aria-pressed={range === r.id}
              className={cn(
                'px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors',
                range === r.id
                  ? 'bg-foreground/10 text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="text-sm text-muted-foreground">Could not load player profiles.</p>
        ) : joinDates === null ? (
          <div className="h-48 animate-pulse bg-muted/40" />
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 font-mono text-xs uppercase tracking-[0.15em] tabular-nums">
              <span className="text-foreground">{fmtMonth(active.date)}</span>
              <span className="text-muted-foreground">
                <span className="text-foreground">{active.joins}</span> new{' '}
                {active.joins === 1 ? 'player' : 'players'}
              </span>
              <span className="text-muted-foreground">
                <span className="text-foreground">{active.total}</span> total
              </span>
              <span className="min-w-0 truncate normal-case tracking-normal text-muted-foreground">
                {active.tournaments.length
                  ? active.tournaments.map((t) => t.name).join(', ')
                  : 'No tournaments started'}
              </span>
            </div>

            <div
              className="flex h-40 items-end gap-0.5 border-b border-border/60"
              onMouseLeave={() => setHovered(null)}
            >
              {buckets.map((b, i) => (
                <button
                  key={b.key}
                  type="button"
                  aria-label={`${fmtMonth(b.date)}: ${b.joins} new players, ${b.tournaments.length} tournaments`}
                  onMouseEnter={() => setHovered(i)}
                  onFocus={() => setHovered(i)}
                  onBlur={() => setHovered(null)}
                  className="group flex h-full min-w-0 flex-1 items-end focus:outline-none"
                >
                  <span
                    className={cn(
                      'block w-full rounded-t-[4px] bg-heat transition-opacity',
                      hovered === null || hovered === i ? 'opacity-90' : 'opacity-40',
                      'group-focus-visible:ring-2 group-focus-visible:ring-ring'
                    )}
                    style={{ height: b.joins ? `${(b.joins / max) * 100}%` : '2px' }}
                  />
                </button>
              ))}
            </div>

            <div className="mt-2 flex gap-0.5" aria-hidden>
              {buckets.map((b, i) => (
                <div key={b.key} className="flex min-w-0 flex-1 flex-col items-center gap-1">
                  <span
                    className={cn(
                      'flex h-5 items-center gap-0.5 text-foreground',
                      !b.tournaments.length && 'invisible',
                      hovered !== null && hovered !== i && 'opacity-40'
                    )}
                  >
                    <Trophy className="h-3 w-3" />
                    {b.tournaments.length > 1 && (
                      <span className="font-mono text-[9px]">{b.tournaments.length}</span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'font-mono text-[9px] uppercase tracking-wider text-muted-foreground',
                      i % labelEvery !== 0 && 'invisible'
                    )}
                  >
                    {fmtMonth(
                      b.date,
                      b.date.getMonth() === 0
                        ? { year: '2-digit', month: 'short' }
                        : { month: 'short' }
                    )}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-sm bg-heat" />
                New players / month · {windowJoins} in range
              </span>
              <span className="flex items-center gap-2">
                <Trophy className="h-3 w-3 text-foreground" />
                Tournament start · {windowEvents} in range
              </span>
              {undated > 0 && <span>{undated} older profiles have no join date</span>}
            </div>

            <details className="mt-4 text-sm">
              <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
                Show as table
              </summary>
              <table className="mt-3 w-full text-left tabular-nums">
                <thead className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <tr>
                    <th className="py-1 pr-4 font-normal">Month</th>
                    <th className="py-1 pr-4 font-normal">New</th>
                    <th className="py-1 pr-4 font-normal">Total</th>
                    <th className="py-1 font-normal">Tournaments</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {[...buckets].reverse().map((b) => (
                    <tr key={b.key}>
                      <td className="py-1 pr-4 font-mono text-xs">{fmtMonth(b.date)}</td>
                      <td className="py-1 pr-4">{b.joins}</td>
                      <td className="py-1 pr-4">{b.total}</td>
                      <td className="py-1 text-muted-foreground">
                        {b.tournaments.map((t) => t.name).join(', ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GrowthChart;
